/* global gsap, ScrollTrigger, EurekaFtuWebGL */
(function () {
  "use strict";

  var STATE = (window.EurekaFtuState = window.EurekaFtuState || {
    progress: 0,
    swapProgress: 0,
    bloomProgress: 0,
    wcdGlow: 0,
    wcdScale: 0.35,
    wcdWash: 0,
    wcdRipple: 0,
  });

  var cleanupFns = [];
  var started = false;

  function prefersReduced() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function q(root, sel) {
    return root.querySelector(sel);
  }

  function qa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function addCleanup(fn) {
    cleanupFns.push(fn);
  }

  function destroy() {
    cleanupFns.forEach(function (fn) {
      try {
        fn();
      } catch (e) {}
    });
    cleanupFns = [];
    if (window.EurekaFtuWebGL && typeof window.EurekaFtuWebGL.unmount === "function") {
      window.EurekaFtuWebGL.unmount();
    }
    if (window.ScrollTrigger) ScrollTrigger.getAll().forEach(function (st) {
      st.kill();
    });
  }

  function wrapStage(root) {
    var first = q(root, ".shopify-section.eureka-ftu-stage");
    if (!first) return null;

    var stack = q(root, ".hero-stage-stack");
    if (!stack) {
      stack = document.createElement("div");
      stack.className = "hero-stage-stack";
      first.parentNode.insertBefore(stack, first);
    }
    /* Pin still runs when Windows maps "Animation effects: off" to reduced-motion. */
    stack.classList.remove("is-static");

    var viewport = q(stack, ".hero-stage-viewport");
    if (!viewport) {
      viewport = document.createElement("div");
      viewport.className = "hero-stage-viewport";
      stack.appendChild(viewport);
    }

    var product = q(root, ".stage-section") && q(root, ".stage-section").closest(".shopify-section");
    var care = q(root, ".care-section") && q(root, ".care-section").closest(".shopify-section");
    var wcd = q(root, ".wcd-section") && q(root, ".wcd-section").closest(".shopify-section");
    var hero = q(root, ".hero-overlay") && q(root, ".hero-overlay").closest(".shopify-section");
    /* Same visual order as Next.js flex `order` (hero → product → WCD → care). */
    [
      [hero, "hero"],
      [product, "product"],
      [wcd, "wcd"],
      [care, "care"],
    ].forEach(function (pair) {
      var el = pair[0];
      if (!el) return;
      el.setAttribute("data-ftu-slot", pair[1]);
      viewport.appendChild(el);
    });

    var wash = q(viewport, ".hero-bright-wash");
    if (!wash) {
      wash = document.createElement("div");
      wash.className = "hero-bright-wash";
      wash.setAttribute("aria-hidden", "true");
    }
    viewport.appendChild(wash);
    var veil = q(viewport, ".hero-bloom-veil");
    if (!veil) {
      veil = document.createElement("div");
      veil.className = "hero-bloom-veil";
      veil.setAttribute("aria-hidden", "true");
    }
    viewport.appendChild(veil);
    return { stack: stack, viewport: viewport };
  }

  function revealMobile(targets, opts) {
    opts = opts || {};
    var delay = opts.delay || 0;
    var stagger = opts.stagger == null ? 0.08 : opts.stagger;
    var y = opts.y == null ? 18 : opts.y;
    var start = opts.start || "top 88%";
    var duration = opts.duration || 0.62;
    var els = gsap.utils.toArray(targets).filter(Boolean);
    els.forEach(function (el, i) {
      gsap.fromTo(
        el,
        { opacity: 0, y: y },
        {
          opacity: 1,
          y: 0,
          duration: duration,
          delay: delay + i * stagger,
          ease: "power2.out",
          immediateRender: true,
          overwrite: "auto",
          scrollTrigger: { trigger: el, start: start, once: true },
        }
      );
    });
  }

  function fadeHeroAiNote(root) {
    var note = q(root, ".hero-ai-note");
    if (!note || prefersReduced()) return;

    var tween = gsap.to(note, {
      opacity: 0,
      duration: 0.28,
      ease: "power2.out",
      paused: true,
      overwrite: "auto",
    });
    var st = ScrollTrigger.create({
      start: 12,
      end: "max",
      onEnter: function () {
        tween.play();
      },
      onLeaveBack: function () {
        tween.reverse();
      },
    });
    addCleanup(function () {
      st.kill();
      tween.kill();
      gsap.set(note, { clearProps: "opacity" });
    });
  }

  function addHeroStoryTweens(tl, els) {
    var sideWidth = function () {
      return window.innerWidth * 0.28;
    };
    if (els.copy) tl.to(els.copy, { opacity: 0, duration: 0.3 }, 0);
    if (els.kv) tl.to(els.kv, { width: "44%", height: "70%", duration: 1 }, 0);
    if (els.left && els.right) {
      tl.to([els.left, els.right], { width: sideWidth, duration: 0.2 }, 0.75);
    }
    if (els.originInner) tl.to(els.originInner, { opacity: 1, y: 0, duration: 0.55 }, 0.95);
    els.cards.forEach(function (card, i) {
      tl.to(card, { opacity: 1, xPercent: 0, duration: 0.45 }, 0.82 + i * 0.12);
    });
  }

  function initNav(root) {
    var nav = q(root, "[data-eureka-nav]");
    if (!nav) return;
    var update = function () {
      nav.classList.toggle("is-scrolled", window.scrollY > 80);
      var overlay = q(root, ".hero-overlay");
      var kv = q(root, ".hero-story-kv");
      if (!(kv instanceof HTMLElement)) {
        nav.classList.remove("is-over-hero");
        return;
      }
      var overlayOpacity = 1;
      if (overlay && typeof gsap !== "undefined") {
        overlayOpacity = Number(gsap.getProperty(overlay, "opacity"));
        if (isNaN(overlayOpacity)) overlayOpacity = 1;
      } else if (overlay) {
        overlayOpacity = parseFloat(window.getComputedStyle(overlay).opacity) || 0;
      }
      var rect = kv.getBoundingClientRect();
      var coversNav = rect.bottom > 72 && rect.top < 72;
      nav.classList.toggle("is-over-hero", overlayOpacity > 0.2 && coversNav);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    if (typeof gsap !== "undefined") gsap.ticker.add(update);
    addCleanup(function () {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      if (typeof gsap !== "undefined") gsap.ticker.remove(update);
    });
  }

  function initHeroKv(root) {
    if (prefersReduced()) return;
    var wrap = q(root, "[data-hero-kv]");
    if (!wrap) return;
    var dust = q(wrap, ".hero-dust-reveal");
    var ring = q(wrap, ".hero-dust-ring");
    var canvas = q(wrap, ".hero-dust-canvas");
    if (!dust || !canvas) return;
    var ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    var IMG_W = 1536;
    var IMG_H = 1024;
    var FINE = "(hover: hover) and (pointer: fine)";
    var MAX = 240;
    var DUST_ZONES = [
      { cx: 0.78, cy: 0.64, rx: 0.28, ry: 0.16 },
      { cx: 0.84, cy: 0.44, rx: 0.2, ry: 0.16 },
      { cx: 0.68, cy: 0.56, rx: 0.16, ry: 0.1 },
      { cx: 0.22, cy: 0.52, rx: 0.12, ry: 0.14 },
      { cx: 0.08, cy: 0.72, rx: 0.14, ry: 0.16 },
    ];
    var SHADES = ["203, 198, 190", "172, 156, 136", "134, 124, 110"];
    var particles = [];
    var pos = { x: 0, y: 0, reveal: 0, intensity: 0 };
    var raf = 0;
    var dpr = 1;
    var lastTs = performance.now();
    var roam = { x: 0, y: 0, vx: 26, vy: 16, ready: false };

    function coverMapping(boxW, boxH) {
      var scale = Math.max(boxW / IMG_W, boxH / IMG_H);
      var dw = IMG_W * scale;
      var dh = IMG_H * scale;
      return { dw: dw, dh: dh, ox: (boxW - dw) / 2, oy: (boxH - dh) / 2 };
    }
    function dustIntensity(sx, sy, boxW, boxH) {
      var m = coverMapping(boxW, boxH);
      var nx = (sx - m.ox) / m.dw;
      var ny = (sy - m.oy) / m.dh;
      var best = 0;
      DUST_ZONES.forEach(function (z) {
        var dx = (nx - z.cx) / z.rx;
        var dy = (ny - z.cy) / z.ry;
        var d = dx * dx + dy * dy;
        if (d <= 1) best = Math.max(best, 1 - Math.sqrt(d));
      });
      return best;
    }
    function setSpotlight(x, y, reveal, boxW, boxH) {
      var intensity = reveal > 0.05 ? dustIntensity(x, y, boxW, boxH) : 0;
      pos = { x: x, y: y, reveal: reveal, intensity: intensity };
      var radius = Math.min(Math.max(72, Math.min(boxW, boxH) * 0.22), 176);
      dust.style.setProperty("--mx", x + "px");
      dust.style.setProperty("--my", y + "px");
      dust.style.setProperty("--reveal", String(reveal));
      dust.style.setProperty("--spot-r", radius + "px");
      if (ring) {
        ring.style.width = radius * 2 + "px";
        ring.style.height = radius * 2 + "px";
        ring.style.transform = "translate(" + x + "px, " + y + "px) translate(-50%, -50%)";
        ring.style.opacity = reveal > 0.05 ? "1" : "0";
        ring.classList.toggle("is-hot", intensity > 0.18);
      }
    }
    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 3);
      var r = wrap.getBoundingClientRect();
      canvas.width = Math.max(1, Math.round(r.width * dpr));
      canvas.height = Math.max(1, Math.round(r.height * dpr));
      canvas.style.width = r.width + "px";
      canvas.style.height = r.height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (!roam.ready) {
        roam.x = r.width * 0.42;
        roam.y = r.height * 0.58;
        roam.ready = true;
      }
    }
    function spawn(cx, cy, radius, heavy) {
      var ang = Math.random() * Math.PI * 2;
      var dist = Math.sqrt(Math.random()) * radius * (heavy ? 0.82 : 0.6);
      var blob = heavy && Math.random() < 0.1;
      return {
        x: cx + Math.cos(ang) * dist,
        y: cy + Math.sin(ang) * dist,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.14 - 0.03,
        size: blob ? 2.5 + Math.random() * 2 : Math.random() < 0.82 ? 0.8 + Math.random() * 1.2 : 2 + Math.random() * 1.5,
        alpha: blob ? 0.32 + Math.random() * 0.28 : heavy ? 0.4 + Math.random() * 0.3 : 0.28 + Math.random() * 0.22,
        life: 0,
        maxLife: blob ? 150 + Math.random() * 110 : 90 + Math.random() * 110,
        wob: Math.random() * Math.PI * 2,
        shade: Math.random(),
        blob: blob,
      };
    }
    function onMove(e) {
      if (!window.matchMedia(FINE).matches) return;
      var rect = wrap.getBoundingClientRect();
      setSpotlight(e.clientX - rect.left, e.clientY - rect.top, 1, rect.width, rect.height);
    }
    function onLeave() {
      if (!window.matchMedia(FINE).matches) return;
      var rect = wrap.getBoundingClientRect();
      setSpotlight(pos.x, pos.y, 0, rect.width, rect.height);
    }
    function tick(now) {
      var dt = Math.min(0.05, (now - lastTs) / 1000);
      lastTs = now;
      var rect = wrap.getBoundingClientRect();
      if (!window.matchMedia(FINE).matches) {
        roam.x += roam.vx * dt;
        roam.y += roam.vy * dt;
        var pad = Math.min(Math.max(72, Math.min(rect.width, rect.height) * 0.22), 176);
        if (roam.x < pad) roam.vx = Math.abs(roam.vx);
        if (roam.x > rect.width - pad) roam.vx = -Math.abs(roam.vx);
        if (roam.y < pad) roam.vy = Math.abs(roam.vy);
        if (roam.y > rect.height - pad) roam.vy = -Math.abs(roam.vy);
        setSpotlight(roam.x, roam.y, 0.92, rect.width, rect.height);
      }
      var r = ring && ring.offsetWidth ? ring.offsetWidth / 2 : 90;
      var heavy = pos.intensity > 0.18;
      var spawnCount =
        pos.reveal < 0.2 ? 0 : heavy ? 5 + Math.floor(pos.intensity * 8) : Math.random() > 0.52 ? (Math.random() > 0.65 ? 2 : 1) : 0;
      for (var i = 0; i < spawnCount && particles.length < MAX; i++) particles.push(spawn(pos.x, pos.y, r, heavy));
      ctx.clearRect(0, 0, rect.width, rect.height);
      if (pos.reveal > 0.05) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r * 0.92, 0, Math.PI * 2);
        ctx.clip();
        for (var p = particles.length - 1; p >= 0; p--) {
          var pt = particles[p];
          pt.life += 1;
          pt.x += pt.vx;
          pt.y += pt.vy;
          var fadeIn = Math.min(1, pt.life / 14);
          var fadeOut = 1 - pt.life / pt.maxLife;
          var a = pt.alpha * fadeIn * fadeOut * pos.reveal;
          if (pt.life >= pt.maxLife || a <= 0.012) {
            particles.splice(p, 1);
            continue;
          }
          ctx.globalAlpha = a;
          ctx.fillStyle = "rgba(" + SHADES[Math.min(SHADES.length - 1, (pt.shade * SHADES.length) | 0)] + ",1)";
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, pt.size, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
        ctx.restore();
      }
      raf = requestAnimationFrame(tick);
    }
    var ro = new ResizeObserver(resize);
    ro.observe(wrap);
    resize();
    wrap.addEventListener("pointermove", onMove);
    wrap.addEventListener("pointerleave", onLeave);
    raf = requestAnimationFrame(tick);
    addCleanup(function () {
      ro.disconnect();
      wrap.removeEventListener("pointermove", onMove);
      wrap.removeEventListener("pointerleave", onLeave);
      cancelAnimationFrame(raf);
    });
  }

  function initSparkles(root) {
    if (prefersReduced()) return;
    qa(root, "[data-sparkles]").forEach(function (host) {
      var canvas = document.createElement("canvas");
      canvas.style.cssText = "position:absolute;inset:0;width:100%;height:100%;pointer-events:none;";
      host.appendChild(canvas);
      var ctx = canvas.getContext("2d");
      if (!ctx) return;
      var dots = [];
      var raf = 0;
      function resize() {
        var r = host.getBoundingClientRect();
        canvas.width = r.width;
        canvas.height = r.height;
        dots = [];
        var n = Math.min(80, Math.floor((r.width * r.height) / 14000));
        for (var i = 0; i < n; i++) {
          dots.push({
            x: Math.random() * r.width,
            y: Math.random() * r.height,
            r: 0.6 + Math.random() * 1.4,
            a: 0.15 + Math.random() * 0.45,
            s: 0.2 + Math.random() * 0.6,
          });
        }
      }
      function tick(t) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        dots.forEach(function (d) {
          var pulse = 0.5 + 0.5 * Math.sin(t * 0.001 * d.s + d.x);
          ctx.fillStyle = "rgba(230,222,167," + d.a * pulse + ")";
          ctx.beginPath();
          ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
          ctx.fill();
        });
        raf = requestAnimationFrame(tick);
      }
      var ro = new ResizeObserver(resize);
      ro.observe(host);
      resize();
      raf = requestAnimationFrame(tick);
      addCleanup(function () {
        ro.disconnect();
        cancelAnimationFrame(raf);
        canvas.remove();
      });
    });
  }

  function initDesktopPin(root, viewport) {
    var kv = q(root, ".hero-story-kv");
    var originInner = q(root, ".origin-col");
    var left = q(root, "#origin");
    var right = q(root, "#unseen");
    var copy = q(root, ".hero-kv-copy");
    var overlay = q(root, ".hero-overlay");
    var stageSection = q(root, ".stage-section");
    var stageCopyJ15 = q(root, ".stage-copy.is-j15");
    var stageCopy830 = q(root, ".stage-copy.is-830");
    var stageWipe = q(root, ".stage-wipe");
    var cards = qa(right || root, ".unseen-card");
    var bloomVeil = q(root, ".hero-bloom-veil");
    var wash = q(root, ".hero-bright-wash");
    var wcd = q(root, ".wcd-section");
    var wcdDeep = q(root, ".wcd-wash-deep");
    var wcdP1 = q(root, ".wcd-p1");
    var wcdTrack = q(root, ".wcd-roll-track");
    var wcdRoll = q(root, ".wcd-roll");
    var wcdRipples = qa(root, ".wcd-ripple");
    var care = q(root, ".care-section");
    var carePin = q(root, ".care-pin");
    var careCopy = q(root, ".care-copy");
    var careFilm = q(root, ".care-film");
    var careTrack = q(root, ".care-track");

    function getFilmMetrics() {
      if (!careFilm || !careTrack) return { travel: 0 };
      var distance = Math.max(0, careTrack.scrollWidth - careFilm.clientWidth);
      var travel = distance + Math.round(careFilm.clientWidth * 0.55);
      return { travel: travel };
    }

    gsap.set(overlay, { autoAlpha: 1, pointerEvents: "auto" });
    gsap.set(stageSection, { autoAlpha: 0 });
    gsap.set(stageCopyJ15, { autoAlpha: 0 });
    gsap.set(stageCopy830, { autoAlpha: 0 });
    if (stageWipe) gsap.set(stageWipe, { clipPath: "inset(0 100% 0 0)" });
    if (bloomVeil) gsap.set(bloomVeil, { autoAlpha: 0, pointerEvents: "none" });
    if (wash) gsap.set(wash, { autoAlpha: 0, pointerEvents: "none" });
    if (wcd) gsap.set(wcd, { autoAlpha: 0 });
    if (wcdDeep) gsap.set(wcdDeep, { autoAlpha: 0 });
    if (wcdP1) gsap.set(wcdP1, { autoAlpha: 0, scale: 0.92, transformOrigin: "50% 50%" });
    if (wcdTrack) gsap.set(wcdTrack, { yPercent: 88 });
    if (wcdRoll) gsap.set(wcdRoll, { autoAlpha: 0 });
    if (wcdRipples.length) gsap.set(wcdRipples, { scale: 0.32, autoAlpha: 0 });
    if (care) gsap.set(care, { autoAlpha: 0, pointerEvents: "none" });
    if (carePin) gsap.set(carePin, { y: window.innerHeight * 0.85 });
    if (careFilm) gsap.set(careFilm, { autoAlpha: 0 });
    if (careTrack) gsap.set(careTrack, { x: 0 });
    if (originInner) gsap.set(originInner, { opacity: 0, y: 56 });
    cards.forEach(function (card) {
      gsap.set(card, { opacity: 0, xPercent: 110 });
    });

    var dolly = { p: 0 };
    var swap = { p: 0 };
    var bloom = { p: 0 };
    var glow = { p: 0, s: 0.35, w: 0, r: 0 };
    STATE.progress = 0;
    STATE.swapProgress = 0;
    STATE.bloomProgress = 0;
    STATE.wcdGlow = 0;
    STATE.wcdScale = 0.35;
    STATE.wcdWash = 0;
    STATE.wcdRipple = 0;

    var tl = gsap.timeline({
      defaults: { ease: "none" },
      scrollTrigger: {
        trigger: q(root, ".hero-stage-stack"),
        start: "top top",
        end: function () {
          var vh = Math.max(window.innerHeight, 1);
          return "+=" + Math.round(vh * 8.4);
        },
        pin: viewport,
        scrub: 1.2,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        refreshPriority: 1,
      },
    });

    addHeroStoryTweens(tl, {
      copy: copy,
      kv: kv,
      left: left,
      right: right,
      originInner: originInner,
      cards: cards,
    });

    tl.addLabel("bright");
    if (wash) tl.to(wash, { autoAlpha: 1, duration: 0.78 }, "bright");
    if (bloomVeil) tl.to(bloomVeil, { autoAlpha: 1, duration: 0.28 }, "bright");
    tl.to(
      bloom,
      {
        p: 1,
        duration: 0.78,
        onUpdate: function () {
          STATE.bloomProgress = bloom.p;
        },
      },
      "bright"
    );

    tl.addLabel("white");
    tl.set(stageSection, { autoAlpha: 1, pointerEvents: "auto" }, "white");
    tl.set(stageCopyJ15, { autoAlpha: 1 }, "white");
    tl.set(overlay, { autoAlpha: 0, pointerEvents: "none" }, "white");

    tl.addLabel("reveal");
    if (wash) tl.to(wash, { autoAlpha: 0, duration: 0.52 }, "reveal");
    tl.to(
      bloom,
      {
        p: 0,
        duration: 0.52,
        onUpdate: function () {
          STATE.bloomProgress = bloom.p;
        },
      },
      "reveal"
    );

    tl.addLabel("dolly");
    if (bloomVeil) tl.set(bloomVeil, { autoAlpha: 0 }, "dolly");
    tl.to(
      dolly,
      {
        p: 1,
        duration: 1.45,
        onUpdate: function () {
          STATE.progress = dolly.p;
        },
      },
      "dolly"
    );

    tl.to({}, { duration: 0.22 });
    tl.addLabel("swap");
    tl.to(
      swap,
      {
        p: 1,
        duration: 1.15,
        onUpdate: function () {
          STATE.swapProgress = swap.p;
        },
      },
      "swap"
    );
    if (stageWipe) {
      tl.fromTo(stageWipe, { clipPath: "inset(0 100% 0 0)" }, { clipPath: "inset(0 0% 0 0)", duration: 0.7 }, "swap");
    }
    if (stageCopyJ15) tl.to(stageCopyJ15, { autoAlpha: 0, duration: 0.45 }, "swap");
    if (stageCopy830) tl.fromTo(stageCopy830, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5 }, "swap+=0.4");

    tl.to({}, { duration: 0.28 });
    tl.addLabel("wcdSat");
    if (wcd) tl.to(wcd, { autoAlpha: 1, duration: 0.2 }, "wcdSat");
    if (stageCopy830) tl.to(stageCopy830, { autoAlpha: 0, duration: 0.4 }, "wcdSat");
    if (wcdDeep) tl.to(wcdDeep, { autoAlpha: 1, duration: 0.7 }, "wcdSat");

    tl.addLabel("wcdGrow", "wcdSat+=0.25");
    tl.to(
      glow,
      {
        p: 1,
        s: 1.12,
        r: 1,
        duration: 0.9,
        onUpdate: function () {
          STATE.wcdGlow = glow.p;
          STATE.wcdScale = glow.s;
          STATE.wcdRipple = glow.r;
        },
      },
      "wcdGrow"
    );
    if (wcdP1) tl.to(wcdP1, { autoAlpha: 1, scale: 1.2, duration: 0.9 }, "wcdGrow");
    wcdRipples.forEach(function (ring, i) {
      tl.fromTo(
        ring,
        { scale: 0.28, autoAlpha: 0 },
        { scale: 3.2 + i * 0.55, autoAlpha: 0.7, duration: 0.85 },
        "wcdGrow+=" + i * 0.16
      );
      tl.to(ring, { autoAlpha: 0, duration: 0.4 }, "wcdGrow+=" + (0.55 + i * 0.16));
    });

    tl.addLabel("wcdP1Exit");
    if (wcdP1) tl.to(wcdP1, { scale: 1.55, autoAlpha: 0, duration: 0.55 }, "wcdP1Exit");
    wcdRipples.forEach(function (ring, i) {
      tl.fromTo(
        ring,
        { scale: 0.6, autoAlpha: 0 },
        { scale: 4.4 + i * 0.5, autoAlpha: 0.45, duration: 0.55 },
        "wcdP1Exit+=" + i * 0.08
      );
    });

    tl.addLabel("wcdRoll");
    if (wcdRoll) tl.to(wcdRoll, { autoAlpha: 1, duration: 0.18 }, "wcdRoll");
    if (wcdTrack) tl.to(wcdTrack, { yPercent: -145, duration: 1.8 }, "wcdRoll");

    tl.addLabel("wcdExpand", "wcdRoll+=1.15");
    if (care) tl.set(care, { autoAlpha: 1, pointerEvents: "auto" }, "wcdExpand");
    tl.to(
      glow,
      {
        w: 1,
        r: 0,
        duration: 0.32,
        onUpdate: function () {
          STATE.wcdWash = glow.w;
          STATE.wcdRipple = glow.r;
        },
      },
      "wcdExpand"
    );
    if (wcd) tl.to(wcd, { autoAlpha: 0, duration: 0.28 }, "wcdExpand+=0.06");
    if (carePin) {
      tl.fromTo(
        carePin,
        { y: function () {
          return window.innerHeight * 0.85;
        } },
        { y: 0, duration: 0.38, immediateRender: false },
        "wcdExpand+=0.08"
      );
    }
    if (careFilm) tl.to(careFilm, { autoAlpha: 1, duration: 0.4 }, "wcdExpand+=0.08");

    if (careFilm && careTrack) {
      var drag = { pointerId: -1, startX: 0, startY: 0, startTrackX: 0, locked: false };
      var endDrag = function (e) {
        if (drag.pointerId !== e.pointerId) return;
        drag.pointerId = -1;
        drag.locked = false;
        careFilm.classList.remove("is-dragging");
        if (careFilm.hasPointerCapture(e.pointerId)) careFilm.releasePointerCapture(e.pointerId);
      };
      var onPointerDown = function (e) {
        if (e.pointerType === "mouse" && e.button !== 0) return;
        if (Number(gsap.getProperty(careFilm, "opacity")) < 0.2) return;
        drag.pointerId = e.pointerId;
        drag.startX = e.clientX;
        drag.startY = e.clientY;
        drag.startTrackX = Number(gsap.getProperty(careTrack, "x")) || 0;
        drag.locked = e.pointerType === "touch" ? false : "x";
        if (drag.locked === "x") {
          careFilm.classList.add("is-dragging");
          careFilm.setPointerCapture(e.pointerId);
        }
      };
      var onPointerMove = function (e) {
        if (drag.pointerId !== e.pointerId) return;
        var dx = e.clientX - drag.startX;
        var dy = e.clientY - drag.startY;
        if (!drag.locked) {
          if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
          if (Math.abs(dy) > Math.abs(dx)) {
            drag.pointerId = -1;
            return;
          }
          drag.locked = "x";
          careFilm.classList.add("is-dragging");
          careFilm.setPointerCapture(e.pointerId);
        }
        if (drag.locked !== "x") return;
        e.preventDefault();
        var travel = getFilmMetrics().travel;
        gsap.set(careTrack, { x: gsap.utils.clamp(-travel, 0, drag.startTrackX + dx) });
      };
      careFilm.addEventListener("pointerdown", onPointerDown);
      careFilm.addEventListener("pointermove", onPointerMove);
      careFilm.addEventListener("pointerup", endDrag);
      careFilm.addEventListener("pointercancel", endDrag);
      careFilm.addEventListener("lostpointercapture", endDrag);
      addCleanup(function () {
        careFilm.removeEventListener("pointerdown", onPointerDown);
        careFilm.removeEventListener("pointermove", onPointerMove);
        careFilm.removeEventListener("pointerup", endDrag);
        careFilm.removeEventListener("pointercancel", endDrag);
        careFilm.removeEventListener("lostpointercapture", endDrag);
        careFilm.classList.remove("is-dragging");
      });
    }

    addCleanup(function () {
      tl.kill();
    });
  }

  function initMobile(root) {
    if (prefersReduced()) return;
    revealMobile(q(root, ".hero-story-kv"), { y: 0, start: "top 99%", duration: 0.8, stagger: 0 });
    revealMobile(q(root, ".hero-kv-copy"), { delay: 0.18, y: 16, start: "top 99%", stagger: 0 });
    revealMobile(q(root, ".origin-col"), { start: "top 86%", stagger: 0 });
    revealMobile(qa(root, ".unseen-card"), { stagger: 0.1, y: 22, start: "top 88%" });
    revealMobile(q(root, ".stage-copy.is-j15"), { y: 16, start: "top 88%", stagger: 0 });
    revealMobile(q(root, ".stage-copy.is-830"), { y: 16, start: "top 88%", stagger: 0 });
    revealMobile(qa(root, ".care-slide"), { stagger: 0.12, y: 28, start: "top 92%", duration: 0.85 });

    var pin = q(root, ".stage-pin");
    if (pin) {
      var st = ScrollTrigger.create({
        trigger: pin,
        start: "top top",
        end: "bottom bottom",
        onUpdate: function (self) {
          STATE.swapProgress = Math.max(0, Math.min(1, (self.progress - 0.38) / 0.28));
        },
      });
      addCleanup(function () {
        st.kill();
      });
    }

    var careSection = q(root, ".care-section");
    var copy = careSection && q(careSection, ".care-copy");
    if (copy) {
      var fromY = function () {
        return window.innerHeight * 0.4;
      };
      gsap.set(copy, { y: fromY() });
      gsap.fromTo(
        copy,
        { y: fromY },
        {
          y: 0,
          ease: "none",
          immediateRender: false,
          scrollTrigger: {
            trigger: careSection,
            start: "top 85%",
            end: function () {
              return "+=" + Math.round(window.innerHeight * 0.4);
            },
            scrub: 1.2,
          },
        }
      );
    }

    var wcdRoot = q(root, ".wcd-section");
    if (!wcdRoot) return;
    var glowEl = q(wcdRoot, ".wcd-glow-css");
    var deep = q(wcdRoot, ".wcd-wash-deep");
    var p1 = q(wcdRoot, ".wcd-p1");
    var roll = q(wcdRoot, ".wcd-roll");
    var track = q(wcdRoot, ".wcd-roll-track");
    var washEl = q(wcdRoot, ".wcd-wash");
    var ripples = qa(wcdRoot, ".wcd-ripple");
    if (glowEl) gsap.set(glowEl, { scale: 0.38, transformOrigin: "50% 50%" });
    if (deep) gsap.set(deep, { autoAlpha: 0 });
    if (p1) gsap.set(p1, { autoAlpha: 0, scale: 0.92, transformOrigin: "50% 50%" });
    if (roll) gsap.set(roll, { autoAlpha: 0 });
    if (track) gsap.set(track, { yPercent: 88 });
    if (ripples.length) gsap.set(ripples, { scale: 0.32, autoAlpha: 0 });

    var tl = gsap.timeline({
      defaults: { ease: "none" },
      scrollTrigger: {
        trigger: wcdRoot,
        start: "top top",
        end: "bottom bottom",
        scrub: 1.2,
        invalidateOnRefresh: true,
      },
    });
    tl.addLabel("wcdGrow");
    if (deep) tl.to(deep, { autoAlpha: 0.7, duration: 0.45 }, "wcdGrow");
    if (glowEl) tl.to(glowEl, { scale: 1.12, duration: 0.9 }, "wcdGrow");
    if (p1) tl.to(p1, { autoAlpha: 1, scale: 1.15, duration: 0.9 }, "wcdGrow");
    ripples.forEach(function (ring, i) {
      tl.fromTo(
        ring,
        { scale: 0.28, autoAlpha: 0 },
        { scale: 3.2 + i * 0.55, autoAlpha: 0.7, duration: 0.85 },
        "wcdGrow+=" + i * 0.16
      );
      tl.to(ring, { autoAlpha: 0, duration: 0.4 }, "wcdGrow+=" + (0.55 + i * 0.16));
    });
    tl.to({}, { duration: 0.35 });
    tl.addLabel("wcdP1Exit");
    if (p1) tl.to(p1, { scale: 1.5, autoAlpha: 0, duration: 0.55 }, "wcdP1Exit");
    ripples.forEach(function (ring, i) {
      tl.fromTo(
        ring,
        { scale: 0.6, autoAlpha: 0 },
        { scale: 4.4 + i * 0.5, autoAlpha: 0.45, duration: 0.55 },
        "wcdP1Exit+=" + i * 0.08
      );
    });
    tl.addLabel("wcdRoll");
    if (roll) tl.to(roll, { autoAlpha: 1, duration: 0.18 }, "wcdRoll");
    if (track) tl.to(track, { yPercent: -145, duration: 3.2 }, "wcdRoll");
    if (ripples.length) tl.to(ripples, { autoAlpha: 0, duration: 0.3 }, "wcdRoll");
    tl.addLabel("wcdExpand", "wcdRoll+=2.35");
    if (glowEl) tl.to(glowEl, { scale: 7.2, duration: 0.42 }, "wcdExpand");
    if (glowEl) tl.to(glowEl, { autoAlpha: 0, duration: 0.22 }, "wcdExpand+=0.28");
    if (deep) tl.to(deep, { autoAlpha: 0, duration: 0.28 }, "wcdExpand");
    if (washEl) tl.to(washEl, { autoAlpha: 0, duration: 0.28 }, "wcdExpand");
    if (roll) tl.to(roll, { autoAlpha: 0, duration: 0.24 }, "wcdExpand+=0.06");
    if (ripples.length) tl.to(ripples, { autoAlpha: 0, duration: 0.18 }, "wcdExpand");
    addCleanup(function () {
      tl.kill();
    });
  }

  function initGiveaway(root) {
    if (prefersReduced()) return;
    var fan = q(root, ".campaign-fan");
    if (!fan) return;
    var mm = gsap.matchMedia();
    mm.add("(min-width: 901px)", function () {
      var cards = qa(fan, ".campaign-card");
      var n = cards.length;
      var stackedX = function (i) {
        return (i - (n - 1) / 2) * 42;
      };
      var stackedRotate = function (i) {
        return (i - (n - 1) / 2) * 7;
      };
      var stackedY = function (i) {
        return Math.abs(i - (n - 1) / 2) * 18;
      };
      var spreadX = function (i) {
        var cardW = cards[0] ? cards[0].offsetWidth : 237;
        var preferredGap = 56;
        var sidePad = 12;
        var usable = Math.max(0, fan.clientWidth - sidePad * 2);
        var maxGap = n > 1 ? (usable - n * cardW) / (n - 1) : preferredGap;
        var gap = Math.max(40, Math.min(preferredGap, maxGap));
        var total = n * cardW + (n - 1) * gap;
        var start = (fan.clientWidth - total) / 2;
        var left = start + i * (cardW + gap);
        return left + cardW / 2 - fan.clientWidth / 2;
      };
      cards.forEach(function (card, i) {
        gsap.set(card, { x: stackedX(i), y: stackedY(i), rotate: stackedRotate(i), zIndex: i + 1 });
      });
      var tl = gsap.timeline({
        defaults: { ease: "none" },
        scrollTrigger: {
          trigger: fan,
          start: "top center",
          end: "top 16%",
          scrub: 1,
          invalidateOnRefresh: true,
        },
      });
      cards.forEach(function (card, i) {
        tl.to(card, { x: function () {
          return spreadX(i);
        }, y: 0, rotate: 0, duration: 1 }, 0);
      });
      tl.to({}, { duration: 1 }, 1);
      return function () {
        tl.kill();
      };
    });
    addCleanup(function () {
      mm.revert();
    });
  }

  function init() {
    if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") return;
    gsap.registerPlugin(ScrollTrigger);
    destroy();
    var root = document.querySelector(".eureka-ftu") || document.body;
    var wrapped = wrapStage(root);
    initNav(root);
    fadeHeroAiNote(root);
    initHeroKv(root);
    initSparkles(root);
    initGiveaway(root);

    var mm = gsap.matchMedia();
    mm.add("(min-width: 768px)", function () {
      if (!wrapped) return;
      initDesktopPin(root, wrapped.viewport);
      return function () {};
    });
    mm.add("(max-width: 767px)", function () {
      initMobile(root);
      return function () {};
    });
    addCleanup(function () {
      mm.revert();
    });

    try {
      if (window.EurekaFtuWebGL && typeof window.EurekaFtuWebGL.mount === "function") {
        window.EurekaFtuWebGL.mount(root, STATE);
      }
    } catch (e) {}
    ScrollTrigger.refresh();
  }

  function boot() {
    if (started) {
      init();
      return;
    }
    started = true;
    init();
    document.addEventListener("shopify:section:load", init);
    document.addEventListener("shopify:section:unload", function () {
      /* full re-init on next load event */
    });
    document.addEventListener("shopify:section:reorder", init);
    document.addEventListener("click", onDiscountLinkClick);
    document.addEventListener("click", onDiscountCopyClick);
    var motionMq = window.matchMedia("(prefers-reduced-motion: reduce)");
    var onMotionChange = function () {
      init();
    };
    if (motionMq.addEventListener) {
      motionMq.addEventListener("change", onMotionChange);
    } else if (motionMq.addListener) {
      motionMq.addListener(onMotionChange);
    }
  }

  function isDiscountHash(href) {
    if (!href) return false;
    if (href === "#discount" || href === "#campaign") return true;
    try {
      var url = new URL(href, window.location.href);
      return (url.hash === "#discount" || url.hash === "#campaign") && url.pathname === window.location.pathname;
    } catch (e) {
      return false;
    }
  }

  function scrollToDiscount() {
    var target = document.getElementById("discount") || document.getElementById("campaign");
    if (!target) return;
    var nav = document.querySelector("[data-eureka-nav]");
    var offset = nav ? Math.ceil(nav.getBoundingClientRect().height) : 72;
    var top = target.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({
      top: Math.max(0, top),
      behavior: prefersReduced() ? "auto" : "smooth",
    });
  }

  function onDiscountCopyClick(e) {
    var btn = e.target.closest && e.target.closest("[data-discount-copy]");
    if (!btn) return;
    var root = btn.closest("[data-discount-code]");
    if (!root) return;
    var toast = root.querySelector(".discount-toast");
    var code = root.getAttribute("data-discount-code") || "";
    var done = function () {
      if (!toast) return;
      toast.hidden = false;
      if (root._ftuCopyTimer) window.clearTimeout(root._ftuCopyTimer);
      root._ftuCopyTimer = window.setTimeout(function () {
        toast.hidden = true;
      }, 2500);
    };
    e.preventDefault();
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done).catch(function () {
        fallbackCopy(code);
        done();
      });
      return;
    }
    fallbackCopy(code);
    done();
  }

  function fallbackCopy(code) {
    var field = document.createElement("textarea");
    field.value = code;
    field.setAttribute("readonly", "");
    field.style.position = "fixed";
    field.style.left = "-9999px";
    document.body.appendChild(field);
    field.select();
    document.execCommand("copy");
    document.body.removeChild(field);
  }

  function onDiscountLinkClick(e) {
    var link = e.target.closest && e.target.closest("a");
    if (!link) return;
    if (!isDiscountHash(link.getAttribute("href"))) return;
    if (!document.getElementById("discount") && !document.getElementById("campaign")) return;
    e.preventDefault();
    scrollToDiscount();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
